/**
 * Presigned-URL transfer primitives.
 *
 * Pure of tar and of sessions — direct R2 HTTP over a presigned URL. The URL is
 * self-authenticating, so these requests carry **no** auth/session headers; the
 * only header that matters is the upload `Content-Type`.
 *
 *   download — GET the URL, stream the body to a file.
 *   upload   — read a file, PUT it to the URL.
 *
 * @module
 */
import { Data, Effect, FileSystem, Stream } from "effect"
import { HttpClient, HttpClientRequest } from "effect/unstable/http"

export class TransferError extends Data.TaggedError("TransferError")<{
  readonly message: string
}> {}

/** GET a presigned URL and stream the response body to `toFile`. */
export const download = (input: {
  readonly url: string
  readonly toFile: string
}): Effect.Effect<void, TransferError, HttpClient.HttpClient | FileSystem.FileSystem> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const response = yield* HttpClient.get(input.url).pipe(
      Effect.mapError(
        (e) => new TransferError({ message: `Download request failed: ${String(e)}` }),
      ),
    )
    if (response.status >= 300) {
      return yield* Effect.fail(
        new TransferError({ message: `Download failed with HTTP ${response.status}` }),
      )
    }
    return yield* Stream.run(response.stream, fs.sink(input.toFile)).pipe(
      Effect.mapError(
        (e) => new TransferError({ message: `Could not save download: ${String(e)}` }),
      ),
    )
  })

/** Read `fromFile` and PUT it to a presigned URL with the given content type. */
export const upload = (input: {
  readonly url: string
  readonly fromFile: string
  readonly contentType: string
}): Effect.Effect<void, TransferError, HttpClient.HttpClient | FileSystem.FileSystem> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const bytes = yield* fs
      .readFile(input.fromFile)
      .pipe(
        Effect.mapError(
          (e) => new TransferError({ message: `Could not read ${input.fromFile}: ${e.message}` }),
        ),
      )

    const request = HttpClientRequest.put(input.url).pipe(
      HttpClientRequest.bodyUint8Array(bytes, input.contentType),
    )
    const response = yield* HttpClient.execute(request).pipe(
      Effect.mapError(
        (e) => new TransferError({ message: `Upload request failed: ${String(e)}` }),
      ),
    )
    if (response.status >= 300) {
      return yield* Effect.fail(
        new TransferError({ message: `Upload failed with HTTP ${response.status}` }),
      )
    }
  })

/** PUT in-memory bytes to a presigned URL with the given content type. */
export const uploadBytes = (input: {
  readonly url: string
  readonly bytes: Uint8Array
  readonly contentType: string
}): Effect.Effect<void, TransferError, HttpClient.HttpClient> =>
  Effect.gen(function* () {
    const request = HttpClientRequest.put(input.url).pipe(
      HttpClientRequest.bodyUint8Array(input.bytes, input.contentType),
    )
    const response = yield* HttpClient.execute(request).pipe(
      Effect.mapError(
        (e) => new TransferError({ message: `Upload request failed: ${String(e)}` }),
      ),
    )
    if (response.status >= 300) {
      return yield* Effect.fail(
        new TransferError({ message: `Upload failed with HTTP ${response.status}` }),
      )
    }
  })
